import type { LifecyclePeriodId } from "./lifecycle.js";
import type { OrgBillingStatus } from "./billing.js";
import type { OrganizationSettings } from "./engine-training-consent.js";
import type { AssignableAdminRole } from "./admin.js";

// ======================================================================================|
// MARK: Organization roles (A7Y-5057)
//
// Three roles. `owner` holds everything; `manager` administers the organization
// and its projects' sensitive configuration (store credentials, publishing,
// billing, members, settings); `standard` works on project content (listings,
// keywords, competitors, ...) without any of the sensitive rights.
//
// Until A7Y-5057 the roles were owner / admin / member / viewer. Stored
// documents may still carry those names until `scripts/migrate-org-role-names.cjs`
// has run against a database, so every read that decides on a role passes the
// stored value through `normalizeOrgRole`.

export const ORG_ROLES = ["owner", "manager", "standard"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

/** Roles an owner or manager can give someone (owner transfer is a separate flow). */
export const ASSIGNABLE_ORG_ROLES = ["manager", "standard"] as const;
export type AssignableOrgRole = (typeof ASSIGNABLE_ORG_ROLES)[number];

/** Roles that administer the organization: members, settings, integrations, activity log. */
export const ORG_MANAGER_ROLES: readonly OrgRole[] = ["owner", "manager"];

/** Pre-A7Y-5057 role names and the role each one is read as. */
export const LEGACY_ORG_ROLE_NAMES: Readonly<Record<string, OrgRole>> = {
  admin: "manager",
  member: "standard",
  viewer: "standard",
};

export function isOrgRole(value: unknown): value is OrgRole {
  return typeof value === "string" && (ORG_ROLES as readonly string[]).includes(value);
}

/**
 * The role a stored value means, or null when it names no role. Accepts the
 * current names and the legacy ones; anything else is null so that every role
 * check refuses it (fail closed).
 */
export function normalizeOrgRole(value: unknown): OrgRole | null {
  if (isOrgRole(value)) return value;
  if (typeof value === "string" && Object.prototype.hasOwnProperty.call(LEGACY_ORG_ROLE_NAMES, value)) {
    return LEGACY_ORG_ROLE_NAMES[value];
  }
  return null;
}

/**
 * A stored membership / invitation / support grant with its `role` read as a
 * current role name. An unrecognised role is left exactly as stored, so no
 * role set ever matches it.
 */
export function withNormalizedOrgRole<T extends { role?: unknown }>(doc: T): T;
export function withNormalizedOrgRole<T extends { role?: unknown }>(doc: T | null | undefined): T | null;
export function withNormalizedOrgRole<T extends { role?: unknown }>(doc: T | null | undefined): T | null {
  if (!doc) return null;
  const role = normalizeOrgRole(doc.role);
  if (!role || role === doc.role) return doc;
  return { ...doc, role };
}

// ======================================================================================|
// MARK: Project capabilities by role (A7Y-2216 D3, A7Y-4613, A7Y-5057)
//
// The single map of what each role may do on a project. The API enforces it
// (`a7y-api/src/common/rbac_acl/app_access.ts`) and the webapp explains and
// pre-gates with it, so the two cannot disagree.

export const PROJECT_CAPABILITIES = [
  "read",
  "edit",
  "publish",
  "manage_credentials",
  "manage_billing",
  "delete_project",
] as const;
export type ProjectCapabilityName = (typeof PROJECT_CAPABILITIES)[number];

// `delete_project` (A7Y-5057, TPF 2026-10-06): deleting a project is an
// Owner/Manager right; every other project setting stays with `edit`.
export const ORG_ROLE_PROJECT_CAPABILITIES: Readonly<Record<OrgRole, readonly ProjectCapabilityName[]>> = {
  owner: ["read", "edit", "publish", "manage_credentials", "manage_billing", "delete_project"],
  manager: ["read", "edit", "publish", "manage_credentials", "manage_billing", "delete_project"],
  standard: ["read", "edit"],
};

export function orgRoleHasProjectCapability(role: unknown, capability: ProjectCapabilityName): boolean {
  const normalized = normalizeOrgRole(role);
  return normalized ? ORG_ROLE_PROJECT_CAPABILITIES[normalized].includes(capability) : false;
}

export function orgRolesWithProjectCapability(capability: ProjectCapabilityName): OrgRole[] {
  return ORG_ROLES.filter((role) => ORG_ROLE_PROJECT_CAPABILITIES[role].includes(capability));
}

// ======================================================================================|
// MARK: Role copy — labels, summaries and the rights grid (A7Y-5057)

export const ORG_ROLE_LABELS: Readonly<Record<OrgRole, string>> = {
  owner: "Owner",
  manager: "Manager",
  standard: "Standard",
};

export const ORG_ROLE_SUMMARIES: Readonly<Record<OrgRole, string>> = {
  owner: "Full control of the organization, including deleting it.",
  manager:
    "Runs the organization: connects stores, publishes, manages billing, members and settings, and can delete projects.",
  standard:
    "Works on projects: listings, keywords, competitors and reports. Cannot touch store credentials, publishing, billing, members or settings, or delete a project.",
};

/** Human label for a stored role value, legacy names included. */
export function orgRoleLabel(value: unknown): string {
  const role = normalizeOrgRole(value);
  return role ? ORG_ROLE_LABELS[role] : "Unknown role";
}

export interface OrgRoleRight {
  id: string;
  label: string;
  /** The roles that hold this right, in `ORG_ROLES` order. */
  roles: readonly OrgRole[];
}

/**
 * The role × rights grid shown wherever a role is chosen or explained. Rows
 * backed by a project capability are derived from
 * `ORG_ROLE_PROJECT_CAPABILITIES`; the organization rows mirror the API's
 * `requireAdmin` (owner + manager) and `requireOwner` route guards.
 */
export const ORG_ROLE_RIGHTS: readonly OrgRoleRight[] = [
  {
    id: "view",
    label: "View projects, listings, keywords, competitors and reports",
    roles: orgRolesWithProjectCapability("read"),
  },
  {
    id: "edit",
    label: "Edit listings, keywords, competitors and project settings",
    roles: orgRolesWithProjectCapability("edit"),
  },
  {
    id: "store_credentials",
    label: "Connect, change or remove App Store and Google Play credentials",
    roles: orgRolesWithProjectCapability("manage_credentials"),
  },
  {
    id: "publish",
    label: "Publish listings and screenshots to the stores",
    roles: orgRolesWithProjectCapability("publish"),
  },
  {
    id: "billing",
    label: "Manage billing and the subscription",
    roles: orgRolesWithProjectCapability("manage_billing"),
  },
  {
    id: "delete_project",
    label: "Delete a project",
    roles: orgRolesWithProjectCapability("delete_project"),
  },
  {
    id: "members",
    label: "Invite and remove members, and change their roles",
    roles: ORG_MANAGER_ROLES,
  },
  {
    id: "org_settings",
    label: "Change organization settings, features and integrations, and view the activity log",
    roles: ORG_MANAGER_ROLES,
  },
  {
    id: "delete_org",
    label: "Delete the organization",
    roles: ["owner"],
  },
];

/** Plain-language list of the roles holding a capability: "an Owner or Manager". */
function rolesPhrase(roles: readonly OrgRole[]): string {
  const labels = roles.map((r) => ORG_ROLE_LABELS[r]);
  if (labels.length === 0) return "nobody";
  if (labels.length === 1) return `an ${labels[0]}`;
  return `an ${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

const CAPABILITY_ACTION: Readonly<Record<ProjectCapabilityName, string>> = {
  read: "view this project",
  edit: "edit this project",
  publish: "publish to the stores",
  manage_credentials: "connect, change or remove store credentials",
  manage_billing: "manage billing",
  delete_project: "delete a project",
};

/** Why a role lacking `capability` cannot act, naming who can: shown beside a disabled control. */
export function projectCapabilityDeniedReason(capability: ProjectCapabilityName): string {
  return `Only ${rolesPhrase(orgRolesWithProjectCapability(capability))} can ${CAPABILITY_ACTION[capability]}.`;
}

/**
 * The one message every store-credential route returns on a 403, and the
 * webapp renders verbatim (A7Y-5057). It names the cause and the remedy.
 */
export const STORE_CREDENTIALS_FORBIDDEN_MESSAGE =
  `${projectCapabilityDeniedReason("manage_credentials")} ` +
  "Your role in this organization does not include that. Ask an Owner or Manager to connect the store, or to change your role.";

export interface Organization {
  id: string;
  name: string;
  slug?: string;
  timezone?: string;
  iconUrl?: string;
  ownerId: string;
  createdAt: string | Date;
  updatedAt?: string | Date;
  metadata?: Record<string, unknown>;
  settings?: OrganizationSettings;
  /**
   * A7Y-5130 — idempotency stamp of the Monday report email: the ISO Monday
   * (`lastHandledWeek`) the org was last handled for, and what came of it.
   */
  weeklyReportEmail?: {
    lastHandledWeek: string;
    handledAt: string;
    outcome: 'sent' | 'nothing_to_report' | 'no_recipients' | 'no_projects';
    emailsSent: number;
  };
  [key: string]: unknown;
}

export interface Membership {
  id: string;
  orgId: string;
  userId: string;
  role: OrgRole;
  joinedAt: string | Date;
  invitedBy?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface OrgInvitation {
  id: string;
  orgId: string;
  email: string;
  role: OrgRole;
  token: string;
  invitedBy: string;
  createdAt: string | Date;
  expiresAt: string | Date;
  acceptedAt?: string | Date;
  acceptedByUserId?: string;
  status: "pending" | "accepted" | "expired" | "revoked";
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * A7Y-4274 — is this invitation still redeemable?
 *
 * `status` alone does not answer that. Nothing sweeps `OrgInvitations`, so a
 * record stays `"pending"` indefinitely; the status only moves to `"expired"`
 * when somebody attempts a redemption and `acceptInvitation()` rejects it.
 * Measured 2026-09-22 in production: 14 documents sat at `"pending"` between
 * 41 and 194 days past `expiresAt`. Any caller that treats `"pending"` as
 * "live" — to render an outstanding-invite list, or to count a seat against a
 * plan's `teamMembers` limit — must apply this instead.
 */
export function isInvitationLive(
  invitation: { status: string; expiresAt: string | Date },
  now: Date = new Date()
): boolean {
  if (invitation.status !== "pending") return false;
  const expiresAt = new Date(invitation.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return false;
  return expiresAt > now;
}

/**
 * A7Y-4970 — the inviter's name as the invitation email and its subject print
 * it: first name plus last name when there is a first name, else the email
 * address, else "A team member". One expression for the invitation route, the
 * reminder enrolment and the render harnesses.
 */
export function inviterDisplayName(
  inviter: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined
): string {
  return inviter?.firstName
    ? `${inviter.firstName}${inviter.lastName ? ` ${inviter.lastName}` : ""}`
    : inviter?.email || "A team member";
}

/**
 * A7Y-1274 — Internal Support Access.
 * A grant that lets a superadmin act inside a customer organization with a
 * specified role. Time-boxed and active only while `revokedAt` is null and
 * `expiresAt` is in the future (A7Y-4388); soft-deleted via `revokedAt`.
 *
 * A7Y-1601 — `reason` is required at grant time (5–500 chars) so every
 * support-access grant carries a SOC2-friendly justification.
 */
export interface SupportAccess {
  id: string;
  orgId: string;
  userId: string;
  role: OrgRole;
  reason: string;
  grantedBy: string;
  grantedAt: string | Date;
  expiresAt: string | Date;
  lastAccessedAt: string | Date | null;
  revokedAt: string | Date | null;
}

/** Wire shape for the admin support-access list endpoint. */
export interface AdminSupportAccessEntry {
  id: string;
  orgId: string;
  userId: string;
  role: OrgRole;
  reason: string;
  grantedBy: string;
  grantedByName?: string;
  grantedByEmail?: string;
  grantedAt: string;
  expiresAt: string;
  lastAccessedAt: string | null;
  user: {
    id: string;
    email?: string;
    name?: string;
  };
}

// A7Y-4388 — support-access grants expire; default and max time-to-live.
export const SUPPORT_ACCESS_DEFAULT_TTL_DAYS = 7;
export const SUPPORT_ACCESS_MAX_TTL_DAYS = 30;

/**
 * A7Y-4388 — is this support-access grant still active?
 *
 * A grant is active only while `revokedAt` is null AND `expiresAt` is in the
 * future. A grant with no `expiresAt` (pre-A7Y-4388) is treated as inactive
 * rather than indefinite — fail closed rather than grant standing access.
 */
export function isSupportAccessActive(
  grant: { revokedAt: string | Date | null; expiresAt?: string | Date | null },
  now: Date = new Date()
): boolean {
  if (grant.revokedAt != null) return false;
  if (grant.expiresAt == null) return false; // pre-A7Y-4388 grant: inactive
  const expiresAt = new Date(grant.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return false; // fail closed
  return expiresAt > now; // expiring exactly now = inactive
}

/** Wire shape returned by GET /admin/users/superadmins (picker source). */
export interface AdminSuperadminUser {
  id: string;
  email?: string;
  name?: string;
}

export interface AdminOrgProject {
  id: string;
  name: string;
  orgId: string;
  status: string;
  iconUrl?: string;
  targetLocales: string[];
  apps?: import("./project.js").ProjectApps;
  createdAt: string;
}

// Admin dashboard types
export interface AdminDashboardUser {
  id: string;
  email: string;
  name: string;
  role: "super_admin" | "admin" | "support";
  mfaEnabled: boolean;
  createdAt: string;
}

export type AdminUserSummary = AdminDashboardUser;

export interface AdminOrganization {
  id: string;
  name: string;
  slug?: string;
  ownerId: string;
  env: "staging" | "production" | null;
  planId: import("./billing.js").PlanId;
  overridePlanId?: import("./billing.js").PlanId | null;
  /**
   * A7Y-1833 — Grant-only effective feature tier = maxPlanTier(planId, overridePlanId).
   * `planId` is the billed plan; this is the entitled tier the org actually has access to.
   */
  entitledPlanId?: import("./billing.js").PlanId;
  billingStatus: import("./billing.js").OrgBillingStatus | null;
  memberCount: number;
  createdAt: string;
  owner?: {
    id: string;
    email: string;
    name?: string;
  };
}

export interface AdminOrgMember {
  membershipId: string;
  userId: string;
  email?: string;
  name?: string;
  role: OrgRole;
  joinedAt: string;
}

export interface AdminOrganizationDetail {
  organization: {
    id: string;
    name: string;
    slug?: string;
    timezone?: string;
    ownerId: string;
    createdAt: string;
    updatedAt?: string;
  };
  ledger: import("./billing.js").OrgLedger | null;
  usage: import("./billing.js").UsageMetrics | null;
  members: AdminOrgMember[];
  transactions: import("./billing.js").LedgerTransaction[];
  projects: AdminOrgProject[];
}

export interface AdminUserSummaryItem {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  env: "staging" | "production" | null;
  createdAt: string;
  membershipCount: number;
  periodId?: LifecyclePeriodId;
}

export interface AdminUserMembership {
  membershipId: string;
  orgId: string;
  /** The org's name, or a placeholder saying why there is none (see `orgNameResolved`). */
  orgName: string;
  /** A7Y-774: false when the org doc is missing or has no name; `orgName` is then a placeholder. */
  orgNameResolved: boolean;
  role: OrgRole;
  joinedAt: string;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    orgId?: string;
    signupContext?: Record<string, unknown>;
    createdAt: string;
    updatedAt?: string;
    /** A7Y-4614: present while the account's deletion is pending on shared custody. */
    accountDeletion?: { status: string; holds: Array<Record<string, unknown>> };
    /** A7Y-4957: admin-site access — full when `superadmin`, else the limited role. */
    superadmin?: boolean;
    adminRole?: AssignableAdminRole | null;
  };
  memberships: AdminUserMembership[];
}

export interface AdminBillingMetrics {
  mrr: number;
  arr: number;
  totalRevenue: number;
  activeSubscriptions: number;
  churnRate: number;
  averageRevenuePerUser: number;
}

export interface AdminSupportTicket {
  id: string;
  subject: string;
  description: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  userId: string;
  userName: string;
  organizationId: string;
  organizationName: string;
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSystemMetrics {
  uptime: number;
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
  activeConnections: number;
  requestsPerMinute: number;
  errorRate: number;
  averageResponseTime: number;
}

export interface OrgAuditLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  organizationId?: string;
  organizationName?: string;
  action: string;
  resource: string;
  resourceId?: string;
  ipAddress: string;
  userAgent: string;
  status: "success" | "failure";
  metadata?: Record<string, unknown>;
  /**
   * A7Y-4275: 90-day retention boundary, matching `AdminAuditLogs` and
   * `server_log`. Written as an ISO8601 string, which the Firestore REST
   * serialiser stores as a timestamp; the TTL policy on `OrgAuditLogs` reaps
   * on this field. Absent on entries that predate the field.
   */
  expiresAt?: string;
}

export type OrgAdminAuditLog = OrgAuditLog;

/**
 * A7Y-4293 — Catalogue of the actions written to `OrgAuditLogs`, with the
 * customer-facing label and filter group for each. The stored `action` stays a
 * plain string so an unknown id still round-trips; the UI humanises it.
 */
export const ORG_AUDIT_ACTIONS = {
  member_remove: { label: "Member removed", category: "members" },
  member_role_change: { label: "Member role changed", category: "members" },
  member_account_deleted: { label: "Member account deleted", category: "members" },
  membership_repair: { label: "Owner membership restored", category: "members" },
  invitation_create: { label: "Invitation sent", category: "invitations" },
  invitation_revoke: { label: "Invitation revoked", category: "invitations" },
  invitation_accept: { label: "Invitation accepted", category: "invitations" },
  store_credential_connect: { label: "Store credential connected", category: "credentials" },
  store_credential_update: { label: "Store credential updated", category: "credentials" },
  store_credential_remove: { label: "Store credential removed", category: "credentials" },
  publish_revision: { label: "Listing published", category: "publishing" },
  publish_screenshots: { label: "Screenshots published", category: "publishing" },
  support_access_grant: { label: "Support access granted", category: "support" },
  support_access_revoke: { label: "Support access revoked", category: "support" },
  support_access_use: { label: "Support access used", category: "support" },
} as const satisfies Record<string, { label: string; category: string }>;

export type OrgAuditAction = keyof typeof ORG_AUDIT_ACTIONS;

export const ORG_AUDIT_ACTION_CATEGORIES = {
  members: "Members",
  invitations: "Invitations",
  credentials: "Store credentials",
  publishing: "Publishing",
  support: "Apptonomy support access",
} as const;

export type OrgAuditActionCategory = keyof typeof ORG_AUDIT_ACTION_CATEGORIES;

/** Who performed an audited action: an organisation member, or Apptonomy support. */
export type OrgAuditActorType = "member" | "support";

/**
 * A7Y-4293 — The customer-facing shape of an audit entry, as served by
 * `GET /org/orgs/:orgId/audit-log`. Retention bookkeeping is dropped and the
 * actor type is made explicit.
 */
export type OrgAuditLogEntry = Omit<OrgAuditLog, "expiresAt"> & {
  actorType: OrgAuditActorType;
};

/** One page of `GET /org/orgs/:orgId/audit-log`. `nextCursor` is opaque. */
export interface OrgAuditLogPage {
  data: OrgAuditLogEntry[];
  nextCursor: string | null;
}

/** Filters accepted by the customer audit-log route and its export. */
export interface OrgAuditLogFilters {
  /** Action ids; an entry matches when its action is any of them. */
  actions?: string[];
  /** Inclusive lower bound: `YYYY-MM-DD` or ISO-8601. */
  from?: string;
  /** Inclusive upper bound: `YYYY-MM-DD` (whole UTC day) or ISO-8601. */
  to?: string;
  /** Keep only entries recorded against this project. */
  projectId?: string;
}

/** Export formats of the customer audit-log route. */
export type OrgAuditLogExportFormat = "csv" | "json";

export interface AdminDashboardStats {
  totalOrganizations: number;
  totalUsers: number;
  activeUsers24h: number;
  mrr: number;
  openTickets: number;
  systemHealth: "healthy" | "warning" | "critical";
}

// ======================================================================================
// MARK: Admin Lifecycle Dashboard Types
// ======================================================================================

export interface AdminLifecyclePeriodCount {
  periodId: LifecyclePeriodId;
  count: number;
}

export interface AdminLifecycleUserItem {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  periodId: LifecyclePeriodId;
  periodEnteredAt: string;
  daysInPeriod: number;
  primaryOrgId: string | null;
  primaryOrgName: string | null;
  billingStatus: OrgBillingStatus | null;
  signals: {
    totalAuditsAcrossOrgs: number;
    daysSinceLastSession: number | null;
  };
}

export interface AdminLifecycleResponse {
  periodCounts: AdminLifecyclePeriodCount[];
  users: AdminLifecycleUserItem[];
  total: number;
  limit: number;
  offset: number;
}
